/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Stack, useStyleConfig, Text} from '@chakra-ui/react'
import Checkbox from '../../../components/checkbox'
import {
    ADD_FILTER,
    REMOVE_FILTER,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {CheckIcon} from '../../../components/icons'

const CheckboxRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const {formatMessage} = useIntl()
    const styles = useStyleConfig('RadioRefinements')

    const [focusedIdx, setFocusedIdx] = React.useState(0)
    const itemRefs = React.useRef([])

    const handleKeyDown = (e, idx, handleToggle) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            const next = (idx + 1) % filter.values.length
            setFocusedIdx(next)
            itemRefs.current[next]?.focus()
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            const prev = (idx - 1 + filter.values.length) % filter.values.length
            setFocusedIdx(prev)
            itemRefs.current[prev]?.focus()
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
        }
    }
    return (
        <Stack {...styles?.stack} spacing={1}>
            {filter?.values?.map((value, idx) => {
                const isChecked = selectedFilters.includes(value.value)
                if (value.hitCount === 0 && !isChecked) return null

                const handleToggle = () =>
                    toggleFilter(value, filter.attributeId, filter.label, isChecked)

                return (
                    <Box
                        as="div"
                        key={idx}
                        ref={(el) => (itemRefs.current[idx] = el)}
                        tabIndex={focusedIdx === idx ? 0 : -1}
                        onFocus={() => setFocusedIdx(idx)}
                        onKeyDown={(e) => handleKeyDown(e, idx, handleToggle)}
                        role="menuitem"
                        onClick={handleToggle}
                        {...styles?.stackItem}
                        cursor="pointer"
                        _focusVisible={{boxShadow: 'outline', bg: 'none'}}
                        _hover={{bg: 'none'}}
                        _focus={{bg: 'none'}}
                    >
                        <Box {...styles?.textContainer}>
                            <Text>{value.label}</Text>
                            <Text {...styles?.filterHit}>
                                {formatMessage(
                                    {
                                        id: 'checkboxRefinements.hitCount',
                                        defaultMessage: '({checkboxHitCount})',
                                    },
                                    {checkboxHitCount: value.hitCount},
                                )}
                            </Text>
                        </Box>
                        <Checkbox
                            icon={isChecked ? <CheckIcon /> : <></>}
                            isChecked={isChecked}
                            onChange={handleToggle}
                            aria-label={formatMessage(
                                isChecked ? REMOVE_FILTER : ADD_FILTER,
                                value,
                            )}
                            pointerEvents="none"
                        />
                    </Box>
                )
            })}
        </Stack>
    )
}

CheckboxRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array,
}

export default CheckboxRefinements
